import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { EditableSlotGroup, RichTextContent, useSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

const ctaDef: SlotGroupDefinition = {
  label: 'Floor Plans CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Label', type: 'text', defaultValue: 'EXPLORE FLOOR PLANS' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const photo1Def: SlotGroupDefinition = {
  label: 'Photo 1',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://irp.cdn-website.com/6fd9ff6f/dms3rep/multi/opt/unnamed-199390c7-640w.jpg',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue:
        'Bathroom with vanity, toilet, towel rack, mirror, and shower curtain in neutral beige tones',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const photo2Def: SlotGroupDefinition = {
  label: 'Photo 2',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://irp.cdn-website.com/6fd9ff6f/dms3rep/multi/opt/Updated+Higher+Res+Image-90e37274-640w.png',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Renovated kitchen with modern cabinets and stainless-steel appliances',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const photo3Def: SlotGroupDefinition = {
  label: 'Photo 3',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://irp.cdn-website.com/6fd9ff6f/dms3rep/multi/opt/Gemini_Generated_Image_jm2dsmjm2dsmjm2d-aef980cc-640w.png',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue:
        'Bright living room with blue chairs, gray sofa, floor lamp, and a view into a bedroom',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const photo4Def: SlotGroupDefinition = {
  label: 'Photo 4',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://irp.cdn-website.com/6fd9ff6f/dms3rep/multi/opt/Gemini_Generated_Image_d63bjd63bjd63bjd-33fa9452-640w.png',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue:
        'Dining area with round table and mustard chairs in an open living room with bright windows',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 3,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const photo5Def: SlotGroupDefinition = {
  label: 'Photo 5',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://irp.cdn-website.com/6fd9ff6f/dms3rep/multi/opt/Gemini_Generated_Image_rkmnrkmnrkmnrkmn-68fdda22-640w.png',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue:
        'Bright bedroom with a made bed, red chair, bedside lamps, framed wall art, and a window with blinds',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 4,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const photo6Def: SlotGroupDefinition = {
  label: 'Photo 6',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://irp.cdn-website.com/6fd9ff6f/dms3rep/multi/opt/Gemini_Generated_Image_ony7f6ony7f6ony7-640w.png',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue:
        'Bright beige bathroom with vanity, mirror, shower curtain, toilet, and towel rack',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 5,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const ctaClassName =
  'inline-flex items-center justify-center min-w-[11rem] px-8 py-3.5 font-body text-sm font-medium tracking-[0.18em] uppercase bg-transparent text-foreground border border-foreground transition-colors duration-300 hover:bg-secondary hover:text-secondary-foreground hover:border-secondary';

function PhotoCell({ image, alt }: { image?: string | null; alt?: string | null }) {
  return image ? (
    <VibesitesImage
      url={image}
      alt={alt || 'Renovated apartment interior'}
      sizes="(max-width: 900px) 33vw, 16vw"
      className="renovated-features__img"
    />
  ) : (
    <div className="h-full w-full bg-muted" />
  );
}

export function RenovatedApartmentFeatures() {
  const cta = useSlotGroup('renovatedapartmentfeatures.cta', ctaDef);
  const photo1 = useSlotGroup('renovatedapartmentfeatures.photo_1', photo1Def);
  const photo2 = useSlotGroup('renovatedapartmentfeatures.photo_2', photo2Def);
  const photo3 = useSlotGroup('renovatedapartmentfeatures.photo_3', photo3Def);
  const photo4 = useSlotGroup('renovatedapartmentfeatures.photo_4', photo4Def);
  const photo5 = useSlotGroup('renovatedapartmentfeatures.photo_5', photo5Def);
  const photo6 = useSlotGroup('renovatedapartmentfeatures.photo_6', photo6Def);

  return (
    <section className="renovated-features bg-[var(--vibe-color-background)] py-10 md:py-14">
      <div className="renovated-features__row mx-auto max-w-7xl px-6 sm:px-8 xl:px-10">
        <div className="renovated-features__content">
          <RichTextContent
            path="renovatedapartmentfeatures.heading"
            as="h2"
            className="renovated-features__heading"
          />
          <RichTextContent
            path="renovatedapartmentfeatures.features"
            as="div"
            className="renovated-features__list"
          />
          <EditableSlotGroup groupPath="renovatedapartmentfeatures.cta" definition={ctaDef}>
            <a
              href={cta.url || '/floor-plans'}
              target={cta._target || '_self'}
              className={ctaClassName}
            >
              {cta.label || 'EXPLORE FLOOR PLANS'}
            </a>
          </EditableSlotGroup>
        </div>

        <div className="renovated-features__grid">
          <EditableSlotGroup
            groupPath="renovatedapartmentfeatures.photo_1"
            definition={photo1Def}
            className="renovated-features__cell"
          >
            <PhotoCell image={photo1.image} alt={photo1.alt} />
          </EditableSlotGroup>
          <EditableSlotGroup
            groupPath="renovatedapartmentfeatures.photo_2"
            definition={photo2Def}
            className="renovated-features__cell"
          >
            <PhotoCell image={photo2.image} alt={photo2.alt} />
          </EditableSlotGroup>
          <EditableSlotGroup
            groupPath="renovatedapartmentfeatures.photo_3"
            definition={photo3Def}
            className="renovated-features__cell"
          >
            <PhotoCell image={photo3.image} alt={photo3.alt} />
          </EditableSlotGroup>
          <EditableSlotGroup
            groupPath="renovatedapartmentfeatures.photo_4"
            definition={photo4Def}
            className="renovated-features__cell"
          >
            <PhotoCell image={photo4.image} alt={photo4.alt} />
          </EditableSlotGroup>
          <EditableSlotGroup
            groupPath="renovatedapartmentfeatures.photo_5"
            definition={photo5Def}
            className="renovated-features__cell"
          >
            <PhotoCell image={photo5.image} alt={photo5.alt} />
          </EditableSlotGroup>
          <EditableSlotGroup
            groupPath="renovatedapartmentfeatures.photo_6"
            definition={photo6Def}
            className="renovated-features__cell"
          >
            <PhotoCell image={photo6.image} alt={photo6.alt} />
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        .renovated-features__row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2.5rem 3rem;
          align-items: start;
        }

        @media (max-width: 900px) {
          .renovated-features__row {
            grid-template-columns: 1fr;
            gap: 1.75rem;
          }
        }

        .renovated-features__heading {
          font-family: var(--vibe-font-heading);
          font-size: clamp(1.5rem, 2.5vw, 2.5rem);
          font-weight: 700;
          color: var(--vibe-color-primary);
          text-transform: uppercase;
          letter-spacing: 0.2em;
          line-height: 1.1;
          margin: 0 0 1.25rem;
        }

        .renovated-features__list {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.6;
          color: var(--vibe-color-text);
          margin: 0 0 1.5rem;
        }

        .renovated-features__list ul {
          list-style: disc;
          padding-left: 1.25rem;
          margin: 0;
          display: grid;
          row-gap: 0.5rem;
        }

        .renovated-features__list p {
          margin: 0 0 0.75em;
        }

        .renovated-features__grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 0.5rem;
        }

        .renovated-features__cell {
          display: block;
          aspect-ratio: 1 / 1;
          overflow: hidden;
        }

        .renovated-features__img {
          display: block;
          width: 100% !important;
          height: 100% !important;
          object-fit: cover;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'RenovatedApartmentFeatures',
  displayName: 'Renovated Apartment Features',
  tokens: {
    'renovatedapartmentfeatures.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Apartment Features</p>',
    },
    'renovatedapartmentfeatures.features': {
      label: 'Feature List',
      type: 'richtext',
      defaultValue:
        '<ul><li>Spacious, open-concept floor plans designed to maximize natural light and create a seamless flow</li><li>Wood-style vinyl plank flooring throughout living spaces for a modern, refined look</li><li>Modern kitchens featuring quartz countertops and stainless-steel appliances</li><li>Kitchen islands ideal for cooking, entertaining, and everyday dining</li><li>Washer and dryer in every home for added convenience</li><li>Generous walk-in closets providing ample storage space</li></ul>',
    },
  },
  slotGroups: {
    'renovatedapartmentfeatures.cta': ctaDef,
    'renovatedapartmentfeatures.photo_1': photo1Def,
    'renovatedapartmentfeatures.photo_2': photo2Def,
    'renovatedapartmentfeatures.photo_3': photo3Def,
    'renovatedapartmentfeatures.photo_4': photo4Def,
    'renovatedapartmentfeatures.photo_5': photo5Def,
    'renovatedapartmentfeatures.photo_6': photo6Def,
  },
};

(
  RenovatedApartmentFeatures as unknown as { contentSchema: ComponentContentSchema }
).contentSchema = contentSchema;

export default RenovatedApartmentFeatures;
